<script lang="ts" setup>
import { computed, markRaw } from 'vue'
import LineCheck from '../../icons/LineCheck.vue'
import LineClock from '../../icons/SolidClock.vue'
import colors from '../../tw-config/colors'
import { classArray } from '../../utils/class'
import type { ComplexClassName } from '../../types/common'
import { AvatarSize, AvatarStatus, AvatarVariant } from './types'
import useAvatar from './useAvatar'

/**
 * Should be kept in sync with AvatarProps in ./types.ts
 * until Vue3/Nuxt3 supports imported proptypes OTB
 */
interface AvatarProps {
  name?: string
  size?: keyof typeof AvatarSize
  status?: keyof typeof AvatarStatus
  variant?: keyof typeof AvatarVariant
  avatarClass?: ComplexClassName
  src?: string
  alt?: string
}

const props = withDefaults(defineProps<AvatarProps>(), {
  name: '',
  size: AvatarSize.md,
  status: AvatarStatus.default,
  variant: AvatarVariant.default,
  avatarClass: '',
  src: '',
  alt: '',
})

const avatarColors = {
  'blue': '#2259F0',
  'light-blue': '#12B2E6',
  'green': '#2CBC63',
  'purple': '#774EE0',
  'lilac': '#AF4FD3',
  'pink': '#E16AC0',
  'red': '#EB4962',
  'orange': '#F1763A',
  'yellow': '#EAC00B',
}

const { classes } = useAvatar({ size: props.size, variant: props.variant })

const computedInitials = computed(() => ([
  // John Doe
  props.name.trim().split(' ').slice(0, 2).map(s => s.substring(0, 1).toUpperCase()).join(''),
  // John
  props.name.trim().substring(0, 2),
].find(p => p.length > 1) || '-'))

const background = computed(() => {
  if (props.src)
    return colors.gray['50']

  if (props.variant === AvatarVariant.colored) {
    const index
      = props.name
        .split('')
        .map(x => x.charCodeAt(0))
        .reduce((acc, cur) => acc + cur, 0)
        % Object.keys(avatarColors).length
    return Object.values(avatarColors)[index]
  }

  return colors.gray['200']
})

const avatarClass = computed(() => classes.value.concat(classArray(props.avatarClass)))

const statusClass = computed(() => {
  const cls = []

  switch (props.status) {
    case AvatarStatus.ready:
      cls.push('bg-status-ready')
      break
    case AvatarStatus.pending:
      cls.push('bg-status-pending')
      break
  }

  switch (props.size) {
    case AvatarSize.xxs:
      cls.push('w-1 h-1')
      break
    case AvatarSize.xs:
      cls.push('w-1.5 h-1.5')
      break
    case AvatarSize.sm:
      cls.push('w-2 h-2')
      break
    case AvatarSize.md:
      cls.push('w-2.5 h-2.5')
      break
    case AvatarSize.lg:
      cls.push('w-3 h-3')
      break
    case AvatarSize.xl:
      cls.push('w-3.5 h-3.5')
      break
    case AvatarSize['2xl']:
      cls.push('w-4 h-4')
      break
  }

  return cls
})

const statusIcon = computed(() => {
  switch (props.status) {
    case AvatarStatus.ready:
      return markRaw(LineCheck)
    case AvatarStatus.pending:
      return markRaw(LineClock)
    default:
      return null
  }
})

const statusIconClass = computed(() => {
  switch (props.size) {
    case AvatarSize.xxs:
    case AvatarSize.xs:
    case AvatarSize.sm:
      return 'hidden'
    case AvatarSize.lg:
      return 'w-2.5'
    case AvatarSize.xl:
      return 'w-3'
    case AvatarSize['2xl']:
      return 'w-3.5'
    case AvatarSize.md:
    default:
      return 'w-2'
  }
})

const statusVisible = computed(() => props.status !== AvatarStatus.default)
</script>

<template>
  <div class="inline-block">
    <div
      class="relative flex items-center justify-center rounded-full font-normal"
      :style="{ background }"
      :class="avatarClass"
      :title="name"
    >
      <template v-if="src">
        <div class="absolute inset-0 overflow-hidden rounded-full">
          <img
            :src="src"
            class="absolute inset-0 size-full rounded-full object-scale-down"
            :alt="alt"
          >
        </div>
      </template>
      <template v-else>
        {{ computedInitials }}
      </template>
      <div
        v-if="statusVisible"
        :class="statusClass"
        class="absolute bottom-0 right-0 flex items-center justify-center rounded-full text-white ring-2 ring-white"
      >
        <Component
          :is="statusIcon"
          v-if="statusIcon"
          :class="statusIconClass"
        />
      </div>
    </div>
  </div>
</template>
